import * as React from 'react';
import dayjs from 'dayjs';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ComputerOutlinedIcon from '@mui/icons-material/ComputerOutlined';
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined';

import useAxios from '../utils/useAxios';
import { SectionCard } from './ui';
import { errorText } from './OrderLines';
import { TRUE_COLORS } from '../theme';

type Session = { id: string; client: 'web' | 'pos'; client_label: string; device: string; created_at: string; last_seen_at: string; current: boolean };

/**
 * The devices where someone is signed in (Receipt-Web-Server management/sessions.py): this panel in a browser, TRUE POS
 * registers. Without `memberId`, mine (close one, or all the others); with it, a team member's (administrators).
 */
const SessionsCard = ({ memberId, name }: { memberId?: number; name?: string }) => {
  const axios = useAxios();
  const [sessions, setSessions] = React.useState<Session[] | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');

  const load = React.useCallback(() => {
    axios.get('management/sessions/', { params: memberId ? { member: memberId } : {} })
      .then(r => setSessions(r.data ?? [])).catch(() => setSessions(null));
  }, [axios, memberId]);
  React.useEffect(load, [load]);

  const act = async (request: () => Promise<unknown>) => {
    setBusy(true);
    setError('');
    try {
      await request();
      load();
    } catch (err) {
      setError(errorText(err, 'No se pudo cerrar la sesión.'));
    } finally {
      setBusy(false);
    }
  };
  if (sessions === null) return null;
  const others = sessions.filter(s => !s.current);

  return (
    <SectionCard title="Sesiones abiertas"
      subtitle={memberId ? `Donde ${name || 'esta persona'} tiene la sesión iniciada.` : 'Los dispositivos donde tienes la sesión iniciada. Cierra los que no reconozcas.'}
      action={(memberId ? sessions.length > 0 : others.length > 0) && (
        <Button size="small" color="error" disabled={busy} data-testid="close-sessions"
          onClick={() => act(() => axios.post('management/sessions/close/', memberId ? { member: memberId } : {}))}>
          {memberId ? 'Cerrar todas' : 'Cerrar las demás'}
        </Button>
      )}>
      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}
      {sessions.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Sin sesiones abiertas.</Typography>
      ) : (
        <Stack divider={<Box sx={{ borderTop: `1px solid ${TRUE_COLORS.borderSolid}` }} />}>
          {sessions.map(session => (
            <Stack key={session.id} direction="row" spacing={1.5} sx={{ py: 1.25, alignItems: 'center' }} data-testid="session">
              <Box sx={{ color: TRUE_COLORS.primary, display: 'grid' }}>
                {session.client === 'pos' ? <PointOfSaleOutlinedIcon /> : <ComputerOutlinedIcon />}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }} noWrap>{session.device || session.client_label}</Typography>
                  {session.current && <Chip size="small" label="Este dispositivo" />}
                </Stack>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {session.client_label} · activa {dayjs(session.last_seen_at).fromNow()}
                </Typography>
              </Box>
              {!session.current && (
                <Button size="small" disabled={busy} onClick={() => act(() => axios.delete(`management/sessions/${session.id}/`))}>Cerrar</Button>
              )}
            </Stack>
          ))}
        </Stack>
      )}
    </SectionCard>
  );
};

export default SessionsCard;
